import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Loader2, Users } from 'lucide-react'

interface MemberPayment {
  id: string
  user_id: string
  display_name: string
  payment_status: string | null
  amount_paid: number | null
  paid_at: string | null
}

interface PaymentTrackerProps {
  leagueId: string
}

export function PaymentTracker({ leagueId }: PaymentTrackerProps) {
  const { toast } = useToast()
  const [members, setMembers] = useState<MemberPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingMembers, setUpdatingMembers] = useState<Set<string>>(new Set())

  const loadMembers = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('league_memberships')
      .select('id, user_id, display_name, payment_status, amount_paid, paid_at')
      .eq('league_id', leagueId)
      .eq('status', 'active')
      .order('display_name')

    if (error) {
      console.error('Error loading members:', error)
    } else {
      setMembers(data || [])
    }
    setLoading(false)
  }, [leagueId])

  useEffect(() => {
    loadMembers()
  }, [loadMembers])

  const togglePaid = async (member: MemberPayment) => {
    setUpdatingMembers((prev) => new Set(prev).add(member.id))
    const isPaid = member.payment_status === 'paid'
    const newStatus = isPaid ? 'pending' : 'paid'
    const paidAt = isPaid ? null : new Date().toISOString()

    setMembers((prev) =>
      prev.map((m) =>
        m.id === member.id
          ? { ...m, payment_status: newStatus, paid_at: paidAt }
          : m,
      ),
    )

    const updateData: Record<string, unknown> = {
      payment_status: newStatus,
      paid_at: paidAt,
    }

    const { error } = await supabase
      .from('league_memberships')
      .update(updateData)
      .eq('id', member.id)

    if (error) {
      toast({ title: 'Failed to update', description: error.message, variant: 'destructive' })
      setMembers((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? { ...m, payment_status: member.payment_status, paid_at: member.paid_at }
            : m,
        ),
      )
    }
    setUpdatingMembers((prev) => {
      const next = new Set(prev)
      next.delete(member.id)
      return next
    })
  }

  const paidCount = members.filter((m) => m.payment_status === 'paid').length

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Payment Tracking
        </CardTitle>
        <CardDescription>
          {paidCount} of {members.length} members paid
          {members.length - paidCount > 0 && (
            <> — {members.length - paidCount} pending</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {members.map((member) => {
            const isPaid = member.payment_status === 'paid'
            const isUpdating = updatingMembers.has(member.id)
            return (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-stone-200 dark:bg-stone-700 rounded-full flex items-center justify-center text-stone-600 dark:text-stone-300 text-sm font-medium">
                    {member.display_name.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-sm">{member.display_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isPaid}
                    onCheckedChange={() => togglePaid(member)}
                    disabled={isUpdating}
                    aria-label={`Mark ${member.display_name} as ${isPaid ? 'unpaid' : 'paid'}`}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
